import React, { useEffect, useState } from 'react';
import { Card, Table, Typography, Tag, Avatar, Spin, Empty, Button, Space, Tooltip } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import tenderDashboardApis from '../../api/tenderDashboard/tenderDashboardApis';

const { Title, Text } = Typography;

interface Evaluator {
  name: string;
}

interface TenderEvalData {
  tenderId: string;
  tenderNumber: string;
  title: string;
  tenderFor: string;
  evaluatorsAssigned: Evaluator[];
  stage: string;
  completion: number;
}

const normaliseStage = (raw?: string): string => {
  if (!raw) return 'Pending';
  const v = raw.trim().toLowerCase();
  if (v === 'completed' || v === 'complete') return 'Completed';
  if (v === 'in progress' || v === 'inprogress' || v === 'in_progress')
    return 'In Progress';
  return 'Pending';
};

const getStageColor = (stage: string) => {
  const map: Record<string, string> = {
    Completed: 'green',
    'In Progress': 'blue',
    Pending: 'orange',
  };
  return map[stage] || 'default';
};

const parseEvaluators = (raw: any): Evaluator[] => {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw
      .map((e: any) =>
        typeof e === 'string' ? { name: e } : { name: e?.name ?? '' }
      )
      .filter((e) => e.name);
  }
  if (typeof raw === 'string') {
    return raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((name) => ({ name }));
  }
  return [];
};

const EvaluationCompletionByTender: React.FC = () => {
  const navigate = useNavigate();
  const currentUser = useSelector((state: any) => state.currentUser?.data);
  const orgId = currentUser?.orgid;

  const [data, setData] = useState<TenderEvalData[] | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const res = await tenderDashboardApis.getEvaluationCompletionByTender({
        orgIds: String(orgId),
      });
      const rows: any[] = res?.data?.data || [];
      const mapped: TenderEvalData[] = rows.map((r) => ({
        tenderId: String(r?.tenderId ?? ''),
        tenderNumber: r?.tenderNumber ?? '',
        title: r?.tenderTitle ?? '',
        tenderFor: r?.tenderFor ?? '',
        evaluatorsAssigned: parseEvaluators(r?.evaluatorsAssigned),
        stage: normaliseStage(r?.evaluationCompletionStatus),
        completion: r?.completionPct ?? 0,
      }));
      setData(mapped);
    } catch (e) {
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const columns = [
    {
      title: 'Tender ID',
      dataIndex: 'tenderNumber',
      key: 'tenderNumber',
      render: (text: string, record: TenderEvalData) => (
        <Tooltip title="Click To View Evaluation Details">
          <span
            className="inlineTableCellLink"
            onClick={() => {
              navigate(`/tender-evaluation/${record.tenderId}`, {
                state: { tenderNumber: record.tenderNumber },
              });
            }}
          >
            {text}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Tender For',
      dataIndex: 'tenderFor',
      key: 'tenderFor',
      render: (type: string) => (
        <Tag color={type === 'Material' ? 'purple' : 'cyan'}>{type}</Tag>
      )
    },
    {
      title: 'Evaluators Assigned',
      dataIndex: 'evaluatorsAssigned',
      key: 'evaluatorsAssigned',
      render: (evaluators: Evaluator[]) => (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {evaluators.map((evaluator, index) => (
            <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Avatar size="small" style={{ backgroundColor: '#004293' }}>
                {evaluator.name
                  .split(' ')
                  .map(n => n[0])
                  .join('')
                  .toUpperCase()}
              </Avatar>
              <Text>{evaluator.name}</Text>
            </div>
          ))}
        </div>
      ),
    },
    {
      title: 'Evaluation Completion',
      dataIndex: 'stage',
      key: 'stage',
      render: (stage: string) => (
        <Tag color={getStageColor(stage)}>{stage}</Tag>
      ),
    },
    {
      title: '% of Completion',
      dataIndex: 'completion',
      key: 'completion',
      render: (value: number) => (
        <Tag color={value === 100 ? 'green' : value === 0 ? 'red' : 'blue'}>
          {value}%
        </Tag>
      ),
    }
  ];

  return (
    <Card
      title={
        <div style={{ padding: '10px 0' }}>
          <Title level={5} style={{ marginBottom: 0, color: '#004293' }}>
            Evaluation Completion % by Tender - Recent 5
          </Title>
          <Text type="secondary" style={{ fontSize: 12, color: '#8094ae' }}>
            Status of ongoing and completed evaluations for latest tenders
          </Text>
        </div>
      }
      extra={
        <Space>
          <Button
            size="small"
            type="link"
            style={{ fontWeight: 500 }}
            onClick={() => navigate('/tender-evaluation')}
          >
            View All
          </Button>
          <ReloadOutlined onClick={fetchData} style={{ cursor: 'pointer' }} />
        </Space>
      }
      bodyStyle={{ padding: '0 14px 14px 14px' }}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin tip="Loading table..." />
        </div>
      ) : !data || data.length === 0 ? (
        <Empty description="No evaluation data found" />
      ) : (
        <Table
          columns={columns}
          dataSource={data.map((d, i) => ({ ...d, key: i }))}
          pagination={false}
          size="middle"
          bordered={false}
          rowClassName={(_, index) => (index % 2 === 0 ? 'ant-table-row-light' : '')}
        />
      )}
    </Card>
  );
};

export default EvaluationCompletionByTender;
